import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit2, Share2, Sparkles, BarChart3, CheckSquare, Square, X, Package } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { TopBar } from '../components/TopBar';
import { BottomNav } from '../components/BottomNav';
import { ProductCard } from '../components/ProductCard';
import { useAuth } from '../contexts/AuthContext';


export const MyProductsScreen = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action');
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkFields, setBulkFields] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [actionSheet, setActionSheet] = useState(null);

  useEffect(() => { fetchProducts(); }, []);
  useEffect(() => { if (action === 'promote' && products.length > 0) navigate(`/marketing/${products[0].id}`); }, [action, products]);

  const fetchProducts = async () => {
    try {
      const res = await api.get('/products');
      setProducts(res.data.products || []);
    } catch { toast.error('Failed to load products'); }
    setLoading(false);
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const shareProduct = async (product) => {
    try {
      const res = await api.get(`/products/${product.id}/share-text`);
      const text = res.data.text;
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      }
    } catch { toast.error('Share failed'); }
    setActionSheet(null);
  };

  const bulkUpdate = async () => {
    if (selected.size === 0) return;
    const fields = {};
    Object.entries(bulkFields).forEach(([k, v]) => { if (v && v.trim()) fields[k] = v.trim(); });
    if (Object.keys(fields).length === 0) { toast.error('Select at least one field to update'); return; }
    setBulkSaving(true);
    try {
      const res = await api.post('/products/bulk-update', { product_ids: [...selected], fields });
      toast.success(`Updated ${res.data.updated} products`);
      setBulkModal(false);
      setBulkMode(false);
      setSelected(new Set());
      setBulkFields({});
      fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk update failed'); }
    setBulkSaving(false);
  };

  if (loading) return <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center"><p>Loading...</p></div>;

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <TopBar title="My Products" showBack={true}
          action={products.length > 0 && (
            <button onClick={() => { setBulkMode(!bulkMode); setSelected(new Set()); }}
              className="text-sm px-3 py-1 rounded-lg bg-white/20 font-medium" data-testid="bulk-toggle">
              {bulkMode ? 'Cancel' : 'Select'}
            </button>
          )} />

        <div className="p-4">
          {/* Bulk bar */}
          {bulkMode && selected.size > 0 && (
            <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-xl p-3 mb-3" data-testid="bulk-bar">
              <span className="text-xs font-bold text-purple-700">{selected.size} selected</span>
              <button onClick={() => setBulkModal(true)} className="h-8 px-4 bg-purple-600 text-white rounded-lg text-xs font-bold" data-testid="bulk-edit-btn">Bulk Edit</button>
            </div>
          )}

          {products.length === 0 ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-12" data-testid="empty-state">
              <Package size={48} className="text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-800 mb-2">No products yet</h3>
              <p className="text-sm text-slate-500 mb-6">Start selling by uploading your first product</p>
              <button onClick={() => navigate('/upload')} className="px-8 py-3 bg-[#128C7E] text-white rounded-xl font-bold text-base active:scale-95" data-testid="upload-first-product-button">Upload Product</button>
            </motion.div>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="products-grid">
              {products.map((product, index) => (
                <motion.div key={product.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }} className="relative">
                  {bulkMode && (
                    <button onClick={() => toggleSelect(product.id)} className="absolute top-2 left-2 z-10" data-testid={`select-${product.id}`}>
                      {selected.has(product.id) ? <CheckSquare size={20} className="text-purple-600 bg-white rounded" /> : <Square size={20} className="text-slate-400 bg-white/80 rounded" />}
                    </button>
                  )}
                  <div onClick={() => !bulkMode && setActionSheet(product)} className="cursor-pointer">
                    <ProductCard product={product} onClick={() => {}} />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Product Action Sheet */}
        {actionSheet && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center" onClick={() => setActionSheet(null)}>
            <div className="w-full max-w-md bg-white rounded-t-2xl p-4 space-y-1 pb-8" onClick={e => e.stopPropagation()} data-testid="action-sheet">
              <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
              <p className="text-sm font-bold text-slate-800 truncate mb-2">{actionSheet.ai_title || 'Product'}</p>
              {[
                { label: 'Edit Product', icon: Edit2, action: () => { setActionSheet(null); navigate(`/product-edit/${actionSheet.id}`); } },
                { label: 'Content Studio', icon: Sparkles, action: () => { setActionSheet(null); navigate(`/content-studio/${actionSheet.id}`); } },
                { label: 'View Readiness', icon: BarChart3, action: () => { setActionSheet(null); navigate(`/product-edit/${actionSheet.id}`); } },
                { label: 'Share on WhatsApp', icon: Share2, action: () => shareProduct(actionSheet) },
              ].map(a => (
                <button key={a.label} onClick={a.action}
                  className="w-full flex items-center gap-3 p-3 rounded-xl text-left active:bg-gray-50 transition-colors" data-testid={`action-${a.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <a.icon size={18} className="text-slate-500" />
                  <span className="text-sm text-slate-700 font-medium">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bulk Edit Modal */}
        {bulkModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={() => setBulkModal(false)}>
            <div className="w-full max-w-md bg-white rounded-t-2xl p-5 space-y-3 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="bulk-modal">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-800">Bulk Edit {selected.size} Products</h3>
                <button onClick={() => setBulkModal(false)}><X size={20} className="text-slate-400" /></button>
              </div>
              <p className="text-xs text-slate-500">Only fill the fields you want to update. Other fields will remain unchanged.</p>
              {[
                { key: 'brand', label: 'Brand', placeholder: 'Brand name' },
                { key: 'category', label: 'Category', placeholder: 'Product category' },
                { key: 'country_of_origin', label: 'Country of Origin', placeholder: 'e.g. India' },
                { key: 'manufacturer', label: 'Manufacturer', placeholder: 'Manufacturer name' },
                { key: 'hsn_code', label: 'HSN Code', placeholder: 'HSN code' },
                { key: 'gst_rate', label: 'GST Rate (%)', placeholder: 'e.g. 5, 12, 18' },
              ].map(f => (
                <div key={f.key} className="space-y-1">
                  <label className="text-xs font-medium text-slate-600">{f.label}</label>
                  <input value={bulkFields[f.key] || ''} onChange={e => setBulkFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.placeholder} className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none" />
                </div>
              ))}
              <button onClick={bulkUpdate} disabled={bulkSaving}
                className="w-full h-12 bg-purple-600 text-white rounded-xl font-bold text-sm disabled:opacity-50 active:scale-95 transition-transform" data-testid="bulk-save-btn">
                {bulkSaving ? 'Updating...' : `Update ${selected.size} Products`}
              </button>
            </div>
          </div>
        )}

        {/* FAB */}
        <button onClick={() => navigate('/upload')} className="fixed bottom-24 right-4 w-14 h-14 bg-[#25D366] text-white rounded-full shadow-lg flex items-center justify-center active:scale-90 transition-transform z-10" data-testid="fab-add">
          <Plus size={24} />
        </button>
      </div>
      <BottomNav />
    </div>
  );
};
